import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-status-dot>` says about itself.
 *
 * The smallest thing in this group: a nine-pixel dot in a semantic colour, with an optional word
 * beside it. It reports the HEALTH of something that is running — a service, an integration, a
 * feed — rather than the state of a record, which is `<caos-status-pill>`'s job.
 *
 * Two details are worth reading before using it.
 *
 * The label is NOT slotted; it comes in through the `label` attribute, and the component renders no
 * `<slot>` at all, so text put inside the element is never drawn.
 *
 * The accessible name falls back to `Status: <intent>` when no label is set — which names the
 * colour band, not the thing. "Status: success" tells a screen-reader user that something is fine
 * without saying what. A dot standing alone therefore wants either a label or a name from the row
 * it sits in.
 *
 * `pulse` is opt-in and is suppressed under `prefers-reduced-motion: reduce`, where the ring is not
 * drawn at all. That is the right fallback here, unlike on the progress bar: the dot itself still
 * carries the whole message, so removing the ring loses nothing.
 */
const docs: ComponentDocs = {
  aliases: ['health indicator', 'online dot', 'connection light', 'led', 'presence dot', 'traffic light'],

  answers: [
    'show whether an integration is connected',
    'show that a service is degraded or down',
    'put a health light beside a name in a list',
    'show that a live feed is running',
  ],

  // The utility bar is included deliberately: a docked strip reporting on a running connection is
  // exactly the case this piece was drawn for, and it is the one frame-adjacent surface that takes
  // ordinary content rather than being a frame part itself.
  placements: ['recordPage', 'homePage', 'appPage', 'utilityBar'],

  inputs: [
    {
      name: 'intent',
      attribute: 'intent',
      type: 'text',
      options: ['success', 'warning', 'danger', 'info', 'muted'],
      description:
        'What the dot means: success (connected, healthy), warning (degraded), danger (down), info ' +
        '(informational), muted (unknown or idle). An unrecognised value draws the muted dot.',
      default: 'muted',
    },
    {
      name: 'label',
      attribute: 'label',
      type: 'text',
      description:
        'The word beside the dot, which also becomes the accessible name. Unset, no text is drawn and ' +
        'the name falls back to naming the intent — see accessibility.',
    },
    {
      name: 'pulse',
      attribute: 'pulse',
      type: 'boolean',
      description:
        'Animate a fading ring out of the dot, for a signal that is genuinely live. Suppressed under ' +
        '`prefers-reduced-motion: reduce`, where the ring is not drawn.',
      default: false,
    },
  ],

  // Read from the source: it reports nothing. It is told the health and draws it.
  events: [],

  // No `<slot>` is rendered; the label comes in through the attribute.
  state: [],

  slots: [],

  tokens: [
    { token: '--caos-color-success', controls: 'The dot when the intent is success.' },
    { token: '--caos-color-warning', controls: 'The dot when the intent is warning.' },
    { token: '--caos-color-danger', controls: 'The dot when the intent is danger.' },
    { token: '--caos-color-info', controls: 'The dot when the intent is info.' },
    { token: '--caos-color-text-muted', controls: 'The dot when the intent is muted, which is also the default.' },
    { token: '--caos-color-text', controls: 'The label, which sits on the neutral surface rather than on a tint.' },
    { token: '--caos-font', controls: 'The typeface the label is set in.' },
    { token: '--caos-radius-pill', controls: 'The roundness of the dot.' },
    { token: '--caos-space-2', controls: 'The gap between the dot and the label.' },
  ],

  parts: [
    { part: 'dot', is: 'the dot' },
    { part: 'label', is: 'the text beside it' },
  ],

  methods: [],

  placing:
    'Inline, immediately before the name of the thing whose health it reports — a row in a list of ' +
    'integrations, a line in a utility strip, a service name in a header. It is `inline-flex` and ' +
    'vertically centred, so it sits on a line of text without moving it. It brings no spacing on ' +
    'either side; the row it sits in owns that.',

  whenToUse:
    'The reachability or health of something that runs continuously, where the whole message fits in a ' +
    'colour and at most one word, and where it needs to be readable in a dense list.',

  insteadUse: [
    {
      name: 'StatusPill',
      when:
        'The subject is a RECORD and its lifecycle state — Draft, Approved, Lost. Those need the word ' +
        'spelled out and they change as somebody works the record; a dot reports a condition that ' +
        'changes on its own.',
    },
    {
      name: 'NoticeBanner',
      when:
        'The outage needs explaining rather than flagging — what is affected, what to do, until when. ' +
        'A dot can only say which of five bands something is in.',
    },
    {
      name: 'ProgressBar',
      when: 'Something is working through a job with an end, rather than sitting in a steady state.',
    },
    {
      name: 'Tag',
      when: 'The chip is a label somebody applied rather than a condition being reported.',
    },
  ],

  accessibility:
    'The element is a `role="img"` with an accessible name, so it is announced as one thing rather than ' +
    'as a stray decorative span, and the dot itself is hidden because the name already carries it. When ' +
    'a `label` is set, that IS the name. Left to the author: setting one — with no label the name ' +
    'becomes `Status: success`, which says a band and not a subject, and a column of those is unusable. ' +
    'The colour is also never the only signal in practice, because the label is what a screen reader ' +
    'reads; a dot with no label in a table needs the name to come from its row.',

  examples: [
    {
      key: 'health',
      label: 'The five bands',
      summary:
        'Every intent with the word it stands for, so a set of integrations can be given colours as a ' +
        'vocabulary rather than one row at a time.',
      placement: [
        { component: 'caos-status-dot', inputs: { intent: 'success', label: 'Connected' } },
        { component: 'caos-status-dot', inputs: { intent: 'warning', label: 'Degraded' } },
        { component: 'caos-status-dot', inputs: { intent: 'danger', label: 'Down' } },
        { component: 'caos-status-dot', inputs: { intent: 'info', label: 'Read only' } },
        { component: 'caos-status-dot', inputs: { intent: 'muted', label: 'Not configured' } },
      ],
    },
    {
      key: 'live',
      label: 'A live signal',
      summary:
        'The opt-in pulse, for a connection that is genuinely streaming rather than merely last known ' +
        'to be up. Under a reduced-motion setting the ring is not drawn and the dot alone remains.',
      placement: { component: 'caos-status-dot', inputs: { intent: 'success', label: 'Streaming from GP', pulse: true } },
    },
  ],
};

export default docs;
